import { Component, type ErrorInfo, type ReactNode } from 'react';
import { AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface Props {
  children: ReactNode;
  /** Optional custom fallback; receives the error and a reset callback. */
  fallback?: (error: Error, reset: () => void) => ReactNode;
}

interface State {
  error: Error | null;
}

/**
 * Catches render-time errors in the subtree so one broken page degrades to a
 * recoverable message instead of white-screening the whole app. Place it around
 * routed page content (see OnboardedOutlet) and key it on the route path so
 * navigating elsewhere remounts it and clears the error.
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // Sentry's global handlers also capture this; keep a console trail too.
    console.error('Unhandled render error:', error, info.componentStack);
  }

  reset = (): void => {
    this.setState({ error: null });
  };

  render(): ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;
    if (this.props.fallback) return this.props.fallback(error, this.reset);

    return (
      <div className="mx-auto max-w-lg py-16 text-center">
        <AlertTriangle className="mx-auto mb-3 h-8 w-8 text-sev-1" />
        <h1 className="mb-1 text-lg font-semibold">Something went wrong</h1>
        <p className="mb-5 text-sm text-muted-foreground">
          This page hit an unexpected error. You can try again, or head back to your network.
        </p>
        <Button onClick={this.reset}>Try again</Button>
      </div>
    );
  }
}

export default ErrorBoundary;
